import { apiService } from './api';
import type { ContentVocabularyItemDto, WordPartOfSpeech } from './contentService';

export interface VocabularyMatchHit {
  id: number;
  entryKey: string;
  persian: string;
  english: string;
  phonetic?: string;
  partOfSpeech: WordPartOfSpeech;
  example?: string;
  imageUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
}

export interface VocabularyAdminRow {
  id: number;
  entryKey: string;
  persian: string;
  english: string;
  phonetic?: string;
  partOfSpeech: WordPartOfSpeech;
  example?: string;
  imageUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  originalAudioUrl?: string;
  voiceStylePresetId?: string | null;
  displayOrder: number;
  sourceKind: 'lesson' | 'book' | 'standalone' | string;
  sourceLabel: string;
  lessonId?: number;
  libraryBookId?: number;
  contentBlockId?: number;
  libraryBookBlockId?: number;
  isTrashed: boolean;
  alsoDefinedCount: number;
  audioConflict: boolean;
}

export interface VocabularyAdminWrite {
  persian: string;
  english: string;
  phonetic?: string;
  partOfSpeech: WordPartOfSpeech;
  example?: string;
  imageUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  originalAudioUrl?: string;
  voiceStylePresetId?: string | null;
}

export interface VocabularyDefinedHit extends VocabularyAdminWrite {
  id: number;
  sourceLabel: string;
}

export const vocabularyService = {
  forLesson(lessonId: number): Promise<ContentVocabularyItemDto[]> {
    return apiService.get<ContentVocabularyItemDto[]>(`/api/Content/vocabulary/by-lesson/${lessonId}`, { cache: false });
  },

  forBook(libraryBookId: number): Promise<ContentVocabularyItemDto[]> {
    return apiService.get<ContentVocabularyItemDto[]>(`/api/Content/vocabulary/by-book/${libraryBookId}`, { cache: false });
  },

  match(body: { text: string; lessonId?: number; libraryBookId?: number }): Promise<VocabularyMatchHit[]> {
    return apiService.post<VocabularyMatchHit[]>('/api/Content/vocabulary/match', body, { cache: false });
  },

  adminList(params: {
    search?: string;
    missingEnglish?: boolean;
    missingAudio?: boolean;
    page?: number;
    pageSize?: number;
  }): Promise<{ items: VocabularyAdminRow[]; total: number }> {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.missingEnglish) query.set('missingEnglish', 'true');
    if (params.missingAudio) query.set('missingAudio', 'true');
    query.set('page', String(params.page ?? 1));
    query.set('pageSize', String(params.pageSize ?? 40));
    return apiService.get<{ items: VocabularyAdminRow[]; total: number }>(
      `/api/Content/vocabulary/admin?${query.toString()}`,
      { cache: false }
    );
  },

  defined(persian: string, excludeId?: number): Promise<VocabularyDefinedHit[]> {
    const query = new URLSearchParams({ persian });
    if (excludeId) query.set('excludeId', String(excludeId));
    return apiService.get<VocabularyDefinedHit[]>(
      `/api/Content/vocabulary/admin/defined?${query.toString()}`,
      { cache: false }
    );
  },

  createStandalone(body: VocabularyAdminWrite): Promise<VocabularyAdminRow> {
    return apiService.post<VocabularyAdminRow>('/api/Content/vocabulary/admin', body, { cache: false });
  },

  update(id: number, body: VocabularyAdminWrite): Promise<VocabularyAdminRow> {
    return apiService.put<VocabularyAdminRow>(`/api/Content/vocabulary/admin/${id}`, body, { cache: false });
  },

  remove(id: number): Promise<void> {
    return apiService.delete(`/api/Content/vocabulary/admin/${id}`);
  }
};
